import { IContainer } from '../interface/container';
import { IEnv } from '../interface/env';

type IEnvStorage = Record<string, unknown> | undefined;

function _queryStorage<T>(env: IEnvStorage, envName: string): T | null {
    const records = env ?? {};
    const value = records[envName] ?? null;
    return value as T | null;
}

function _getValue<T>(envStorage: Array<IEnvStorage>, envName: string): T | null {
    /* prefer Window env over process */
    let value: T | null = null;

    for (let idx = 0; idx < envStorage.length; idx++) {
        value = _queryStorage<T>(envStorage[idx], envName);
        if (value) {
            break;
        }
    }

    return value;
}

function _getValueWithDefault<T>(
    envStorage: Array<IEnvStorage>,
    envName: string,
    defaultValue: T,
): T {
    return _getValue<T>(envStorage, envName) ?? defaultValue;
}

function _getNumber(envStorage: Array<IEnvStorage>, name: string, defaultValue = -1): number {
    return _getValue<number>(envStorage, name) ?? defaultValue;
}

function _getBoolean(envStorage: Array<IEnvStorage>, name: string, defaultValue = false): boolean {
    return _getValue<boolean>(envStorage, name) ?? defaultValue;
}

function _getString(envStorage: Array<IEnvStorage>, name: string, defaultValue = ''): string {
    return _getValue<string>(envStorage, name) ?? defaultValue;
}

function env(container: IContainer): IEnv {
    const isRunningOnNodeJS = typeof window === 'undefined';

    /* eslint-disable no-restricted-globals */
    const globalObject: any = isRunningOnNodeJS ? globalThis : window;
    /* eslint-enable no-restricted-globals */

    /* expose the process variables on the window */
    const processEnv = process?.env ?? {};

    const envStorage: Array<IEnvStorage> = [
        globalObject?.env ?? globalObject?.ENV ?? {},
        processEnv,
    ];

    const module: IEnv = {
        getValue: (envName: string) => _getValue(envStorage, envName),
        getValueWithDefault: <T>(envName: string, defaultValue: T) =>
            _getValueWithDefault(envStorage, envName, defaultValue),
        getNumber: (name: string, defaultValue?: number) =>
            _getNumber(envStorage, name, defaultValue ?? -1),
        getBoolean: (name: string, defaultValue?: boolean) =>
            _getBoolean(envStorage, name, defaultValue ?? false),
        getString: (name: string, defaultValue?: string) =>
            _getString(envStorage, name, defaultValue ?? ''),
    };

    return module;
}

export default env;
